import React, { useCallback, useState } from 'react';
import posthog from 'posthog-js';
import { Icon } from './ui/Icon.jsx';
import { useApplications, useApproveApplication, useRejectApplication, useCreateTokenForApproved, useToggleExport } from '../hooks/useApplications.js';
import RetailerContacts from './admin/RetailerContacts.jsx';
import { ViewAsCustomerLink } from './admin/ViewAsCustomerLink.jsx';
import { useOrderRecipients } from '../hooks/useOrderRecipients.js';
import { catalogueUrl } from '../lib/utils.js';

// ─── STATUS FILTER TABS ────────────────────────────────────────────────────────
const STATUS_TABS = [
  { key: 'pending',  labelEn: 'Pending',  labelAr: 'قيد الانتظار', color: 'var(--status-warn)', bg: 'var(--status-warn-bg)' },
  { key: 'approved', labelEn: 'Approved', labelAr: 'مقبول',        color: 'var(--status-success)', bg: 'var(--status-success-bg)' },
  { key: 'rejected', labelEn: 'Rejected', labelAr: 'مرفوض',        color: 'var(--status-danger)', bg: 'var(--status-danger-bg)' },
  { key: 'all',      labelEn: 'All',      labelAr: 'الكل',          color: 'var(--sedra-olive)', bg: 'var(--sedra-cream)' },
];

const STATUS_BADGE = {
  pending:  { color: 'var(--status-warn)', bg: 'var(--status-warn-bg)', labelEn: 'Pending',  labelAr: 'قيد الانتظار' },
  approved: { color: 'var(--status-success)', bg: 'var(--status-success-bg)', labelEn: 'Approved', labelAr: 'مقبول' },
  rejected: { color: 'var(--status-danger)', bg: 'var(--status-danger-bg)', labelEn: 'Rejected', labelAr: 'مرفوض' },
};

const cardStyle = {
  background: '#fff', borderRadius: 10,
  border: '1px solid #ece8e1',
  padding: '16px 18px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  transition: 'box-shadow 0.15s',
};

const badgeStyle = (color, bg) => ({
  display: 'inline-block', padding: '2px 10px', borderRadius: 20,
  fontSize: 11, fontWeight: 700, color, background: bg, letterSpacing: 0.3,
});

// A token the admin can actually use: active AND not expired. Prefer the
// newest so a re-generated link wins over an older one still marked active
// (nothing revokes the old row on re-generate).
const usableToken = (tokens) => {
  const now = Date.now();
  return tokens
    .filter(t => t.active && (!t.expires_at || new Date(t.expires_at).getTime() > now))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null;
};

// ─── APPROVE MODAL ─────────────────────────────────────────────────────────────
function ApproveModal({ application, lang, onConfirm, onClose, isPending }) {
  const ar = lang === 'ar';
  const [tokenType, setTokenType] = useState('standard');

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget && !isPending) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(30,40,26,0.70)',
        backdropFilter: 'blur(4px)', zIndex: 3000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 14, padding: '30px 30px 26px',
        width: '100%', maxWidth: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        fontFamily: ar ? "'Tajawal', sans-serif" : "'Inter', sans-serif",
        direction: ar ? 'rtl' : 'ltr',
      }}>
        <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--sedra-forest)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, fontFamily: ar ? "'Almarai', 'Tajawal', sans-serif" : undefined }}>
          <Icon name="CheckCircle2" size={17} /> {ar ? 'الموافقة على الطلب' : 'Approve Application'}
        </div>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
          {application.company_name} — {application.contact_name}
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {ar ? 'نوع رابط الأسعار' : 'Catalogue Token Type'}
          </label>
          {[
            { value: 'standard', labelEn: 'Standard Wholesale (no VAT)', labelAr: 'جملة عادي (بدون ضريبة)' },
            { value: 'vat',      labelEn: 'VAT Wholesale (+14%)',         labelAr: 'جملة مع ضريبة (+14%)' },
          ].map(opt => (
            <label key={opt.value} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
              borderRadius: 8, border: `2px solid ${tokenType === opt.value ? 'var(--sedra-forest-soft)' : 'var(--sedra-line)'}`,
              marginBottom: 8, cursor: 'pointer', transition: 'border-color 0.15s',
              background: tokenType === opt.value ? '#f4f6f0' : '#fff',
            }}>
              <input type="radio" name="tokenType" value={opt.value}
                checked={tokenType === opt.value}
                onChange={() => setTokenType(opt.value)}
                style={{ accentColor: 'var(--sedra-forest-soft)' }}
              />
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sedra-forest)' }}>
                {ar ? opt.labelAr : opt.labelEn}
              </span>
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => onConfirm(tokenType)}
            disabled={isPending}
            style={{
              flex: 1, padding: '12px', borderRadius: 8,
              background: isPending ? '#aaa' : 'var(--sedra-olive)',
              color: '#fff', border: 'none',
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontSize: 14, fontWeight: 700,
              transition: 'background 0.2s',
            }}
          >
            {isPending ? (ar ? 'جاري…' : 'Processing…') : (ar ? 'الموافقة وإنشاء الرابط' : 'Approve & Generate Link')}
          </button>
          <button
            onClick={onClose}
            disabled={isPending}
            style={{
              padding: '12px 18px', borderRadius: 8,
              background: 'var(--sedra-cream)', color: 'var(--sedra-forest-soft)',
              border: '1.5px solid var(--sedra-line)',
              cursor: 'pointer', fontSize: 14, fontWeight: 600,
            }}
          >
            {ar ? 'إلغاء' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── APPLICATION CARD ──────────────────────────────────────────────────────────
// Memoized so toggling one card's Contacts panel (or typing elsewhere) doesn't
// re-render the whole list. Owns its own contacts-open state for the same
// reason — a per-card toggle re-renders exactly this card.
const ApplicationCard = React.memo(function ApplicationCard({
  app, lang, justApprovedToken,
  onApprove, onReject, onGenerateLink, onToggleExport,
  approvePending, rejectPending, createTokenPending, toggleExportPending,
}) {
  const ar = lang === 'ar';
  const [contactsOpen, setContactsOpen] = useState(false);
  const sb = STATUS_BADGE[app.status] || STATUS_BADGE.pending;
  const tokens = Array.isArray(app.catalogue_tokens) ? app.catalogue_tokens : [];
  const activeToken = usableToken(tokens);
  const isApproved = app.status === 'approved';

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        {/* Left: application details */}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--sedra-forest)' }}>{app.company_name}</span>
            <span style={badgeStyle(sb.color, sb.bg)}>{ar ? sb.labelAr : sb.labelEn}</span>
            {/* Token link status badge — visible on all approved apps */}
            {isApproved && (
              activeToken ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--status-info)', background: 'var(--status-info-bg)', padding: '2px 8px', borderRadius: 20 }}>
                  <Icon name="Link" size={11} /> {ar ? 'رابط نشط' : 'Active Link'}
                  <span style={{ fontSize: 9, color: '#0ea5e9', fontWeight: 400, textTransform: 'uppercase' }}>{activeToken.type}</span>
                </span>
              ) : (
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--status-warn)', background: 'var(--status-warn-bg)', padding: '2px 8px', borderRadius: 20, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="TriangleAlert" size={11} /> {ar ? 'بدون رابط' : 'No Link'}
                </span>
              )
            )}
          </div>
          <div style={{ fontSize: 13, color: '#555', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="User" size={12} /> {app.contact_name}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="Mail" size={12} /> {app.email}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="Phone" size={12} /> {app.phone}</span>
            {app.tax_id && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="FileText" size={12} /> {app.tax_id}</span>}
            <span style={{ color: '#aaa', fontSize: 11, marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon name="Clock" size={11} /> {new Date(app.created_at).toLocaleString(ar ? 'ar-EG' : 'en-GB', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
                timeZone: 'Africa/Cairo',
              })}
            </span>
          </div>

          {isApproved && (
            <>
              {/* Export permission toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 12, color: '#666', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="ChartColumn" size={12} /> {ar ? 'تصدير الكتالوج:' : 'Catalog Export:'}
                </span>
                <button
                  onClick={() => onToggleExport(app.id, !app.can_export_data)}
                  disabled={toggleExportPending}
                  style={{
                    padding: '3px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
                    fontWeight: 700, fontSize: 11,
                    background: app.can_export_data ? 'var(--status-success-bg)' : '#f3f4f6',
                    color: app.can_export_data ? 'var(--status-success)' : '#6b7280',
                    transition: 'all 0.2s',
                  }}>
                  {app.can_export_data ? (ar ? 'مفعّل' : 'Enabled') : (ar ? 'معطّل' : 'Disabled')}
                </button>
              </div>

              {/* Active token row — copy link inline */}
              {!justApprovedToken && activeToken && (
                <div style={{ marginTop: 10, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '9px 12px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--status-info)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="Link" size={11} /> {ar ? 'الرابط النشط' : 'Active Link'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <code style={{ fontSize: 11, color: '#0c4a6e', background: 'var(--status-info-bg)', padding: '3px 8px', borderRadius: 4, wordBreak: 'break-all', flex: 1 }}>
                      {catalogueUrl(activeToken.token)}
                    </code>
                    <button onClick={() => navigator.clipboard.writeText(catalogueUrl(activeToken.token))}
                      style={{ padding: '4px 10px', borderRadius: 6, background: 'var(--status-info)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {ar ? 'نسخ' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              {/* Just-approved new token */}
              {justApprovedToken && (
                <div style={{ marginTop: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--status-success)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="CheckCircle2" size={11} /> {ar ? 'تم إنشاء الرابط' : 'Token Generated'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <code style={{ fontSize: 12, color: '#166534', background: 'var(--status-success-bg)', padding: '3px 8px', borderRadius: 4, wordBreak: 'break-all' }}>
                      {catalogueUrl(justApprovedToken)}
                    </code>
                    <button onClick={() => navigator.clipboard.writeText(catalogueUrl(justApprovedToken))}
                      style={{ padding: '4px 10px', borderRadius: 6, background: 'var(--status-success)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {ar ? 'نسخ' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right: action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignSelf: 'center' }}>
          {app.status === 'pending' && (
            <>
              <button onClick={() => onApprove(app)} disabled={approvePending}
                style={{ padding: '9px 18px', borderRadius: 8, background: 'var(--status-success)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name="CheckCircle2" size={13} /> {ar ? 'موافقة' : 'Approve'}
              </button>
              <button onClick={() => onReject(app)} disabled={rejectPending}
                style={{ padding: '9px 18px', borderRadius: 8, background: '#fff', color: 'var(--status-danger)', border: '1.5px solid #fca5a5', cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name="X" size={13} /> {ar ? 'رفض' : 'Reject'}
              </button>
            </>
          )}
          {isApproved && (
            <>
              {/* Generate Link — no usable token yet */}
              {!activeToken && !justApprovedToken && (
                <button onClick={() => onGenerateLink(app)} disabled={createTokenPending}
                  style={{ padding: '9px 18px', borderRadius: 8, background: 'var(--status-info)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="Link" size={13} /> {ar ? 'إنشاء رابط' : 'Generate Link'}
                </button>
              )}
              {/* View as customer — opens THEIR catalogue with all their features */}
              <ViewAsCustomerLink token={activeToken?.token || justApprovedToken} lang={lang} />
              {/* Contacts — people at this account (handles staff turnover) */}
              <button onClick={() => setContactsOpen(o => !o)}
                style={{ padding: '9px 18px', borderRadius: 8, background: contactsOpen ? 'var(--sedra-olive)' : 'var(--sedra-olive-wash)', color: contactsOpen ? '#fff' : 'var(--sedra-olive-deep)', border: '1.5px solid var(--sedra-line)', cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name="Users" size={13} /> {ar ? 'جهات الاتصال' : 'Contacts'}
              </button>
            </>
          )}
        </div>
      </div>
      {isApproved && contactsOpen && (
        <RetailerContacts accountId={app.id} lang={lang} />
      )}
    </div>
  );
});

// ─── APPLICATIONS ──────────────────────────────────────────────────────────────
export default function Applications({ lang }) {
  const ar = lang === 'ar';
  const [statusFilter, setStatusFilter] = useState('pending');
  const [approveTarget, setApproveTarget] = useState(null); // application row being approved
  const [approvedTokens, setApprovedTokens] = useState({}); // { appId: token } for just-approved rows
  const [copied, setCopied] = useState(false);

  const { data: applications = [], isLoading, isError, isFetching } = useApplications({ status: statusFilter });
  const approveMutation      = useApproveApplication();
  const rejectMutation       = useRejectApplication();
  const createTokenMutation  = useCreateTokenForApproved();
  const toggleExportMutation = useToggleExport();
  // generateLinkTarget: the approved app that needs a new token (reuses ApproveModal)
  const [generateLinkTarget, setGenerateLinkTarget] = useState(null);
  // Re-issuing a catalogue link must email the account's CURRENT primary
  // contact — the application row still names whoever originally applied, so
  // sending there posts the link to someone who may have left the company.
  const { contactsForAccount } = useOrderRecipients();
  const primaryFor = (accountId) => contactsForAccount(accountId).find(c => c.is_primary) || null;

  const handleApprove = async (tokenType) => {
    if (!approveTarget) return;
    try {
      const result = await approveMutation.mutateAsync({
        applicationId:     approveTarget.id,
        applicantEmail:    approveTarget.email,
        applicantName:     approveTarget.contact_name,
        applicantPhone:    approveTarget.phone,
        companyName:       approveTarget.company_name,
        tokenType,
        preferredLanguage: approveTarget.preferred_language || 'ar',
      });
      posthog.capture('retailer_application_approved', {
        application_id: approveTarget.id,
        token_type: tokenType,
        preferred_language: approveTarget.preferred_language || 'ar',
      });
      setApprovedTokens(prev => ({ ...prev, [approveTarget.id]: result.token }));
      setApproveTarget(null);
    } catch (err) {
      alert((ar ? 'حدث خطأ: ' : 'Error: ') + err.message);
    }
  };

  // Stable callbacks so the memoized cards don't re-render when this component
  // does (mutateAsync/mutate are referentially stable in react-query v5).
  const handleReject = useCallback(async (app) => {
    if (!window.confirm(ar ? `هل تريد رفض طلب ${app.company_name}؟` : `Reject application from ${app.company_name}?`)) return;
    try {
      await rejectMutation.mutateAsync({ applicationId: app.id });
      posthog.capture('retailer_application_rejected', {
        application_id: app.id,
        preferred_language: app.preferred_language || 'ar',
      });
    } catch (err) {
      alert((ar ? 'حدث خطأ: ' : 'Error: ') + err.message);
    }
  }, [ar, rejectMutation.mutateAsync]);

  const handleToggleExport = useCallback((applicationId, value) => {
    toggleExportMutation.mutate({ applicationId, value });
  }, [toggleExportMutation.mutate]);

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: 'var(--sedra-forest)', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="ClipboardList" size={18} /> {ar ? 'طلبات الوصول للجملة' : 'Wholesale Applications'}
          </h2>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: '#999' }}>
            {ar ? 'راجع طلبات التجار وأنشئ روابط الأسعار لهم' : 'Review retailer applications and generate pricing links'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.origin + '/register').then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              });
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: copied ? 'var(--status-success-bg)' : 'var(--sedra-olive-wash)',
              color: copied ? 'var(--status-success)' : 'var(--sedra-olive-deep)',
              border: `1px solid ${copied ? 'var(--status-success)' : 'var(--sedra-line)'}`,
              borderRadius: 8, padding: '7px 14px',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.15s',
            }}>
            {copied
              ? (<><Icon name="Check" size={14} /> {ar ? 'تم' : 'Copied'}</>)
              : (<><Icon name="Link" size={14} /> {ar ? 'نسخ رابط التقديم' : 'Copy application link'}</>)}
          </button>
          {isFetching && !isLoading && (
            <span style={{ fontSize: 12, color: '#aaa', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="RefreshCw" size={11} /> {ar ? 'تحديث…' : 'Refreshing…'}</span>
          )}
        </div>
      </div>

      {/* Status tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {STATUS_TABS.map(tab => (
          <button key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            style={{
              padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, transition: 'all 0.15s',
              background: statusFilter === tab.key ? tab.color : 'var(--sedra-cream)',
              color:      statusFilter === tab.key ? '#fff' : '#666',
              boxShadow:  statusFilter === tab.key ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
            }}>
            {ar ? tab.labelAr : tab.labelEn}
          </button>
        ))}
      </div>

      {/* Body */}
      {isLoading ? (
        <div style={{ color: '#aaa', padding: 40, textAlign: 'center' }}>
          {ar ? 'جاري التحميل…' : 'Loading…'}
        </div>
      ) : isError ? (
        <div style={{ color: 'var(--status-danger)', padding: 40, textAlign: 'center' }}>
          {ar ? 'حدث خطأ في تحميل البيانات' : 'Failed to load applications'}
        </div>
      ) : applications.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#bbb', padding: 60, fontSize: 15 }}>
          {ar ? 'لا توجد طلبات في هذا القسم' : 'No applications in this category'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {applications.map(app => (
            <ApplicationCard
              key={app.id}
              app={app}
              lang={lang}
              justApprovedToken={approvedTokens[app.id]}
              onApprove={setApproveTarget}
              onReject={handleReject}
              onGenerateLink={setGenerateLinkTarget}
              onToggleExport={handleToggleExport}
              approvePending={approveMutation.isPending}
              rejectPending={rejectMutation.isPending}
              createTokenPending={createTokenMutation.isPending}
              toggleExportPending={toggleExportMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Approve modal — for pending applications */}
      {approveTarget && (
        <ApproveModal
          application={approveTarget}
          lang={lang}
          onConfirm={handleApprove}
          onClose={() => setApproveTarget(null)}
          isPending={approveMutation.isPending}
        />
      )}

      {/* Generate Link modal — reuses ApproveModal for approved customers with no token */}
      {generateLinkTarget && (
        <ApproveModal
          application={generateLinkTarget}
          lang={lang}
          isPending={createTokenMutation.isPending}
          onClose={() => setGenerateLinkTarget(null)}
          onConfirm={async (tokenType) => {
            try {
              // Send the link to whoever holds the account TODAY, not to the
              // person who originally applied.
              const pc = primaryFor(generateLinkTarget.id);
              const result = await createTokenMutation.mutateAsync({
                applicationId:     generateLinkTarget.id,
                applicantEmail:    pc?.email || generateLinkTarget.email,
                applicantName:     pc?.name  || generateLinkTarget.contact_name,
                applicantPhone:    pc?.phone || generateLinkTarget.phone,
                companyName:       generateLinkTarget.company_name,
                tokenType,
                preferredLanguage: generateLinkTarget.preferred_language || 'ar',
              });
              setApprovedTokens(prev => ({ ...prev, [generateLinkTarget.id]: result.token }));
              setGenerateLinkTarget(null);
            } catch (err) {
              alert((ar ? 'حدث خطأ: ' : 'Error: ') + err.message);
            }
          }}
        />
      )}
    </div>
  );
}
